// src/pages/GroupMembers.js
import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  HourglassTop,
  PersonAdd,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { getData, postData } from "../services/request";
import { getRoleLabel, getStoredUser, getUserRole } from "../auth/roles";

const GroupMembers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // Consi request par action ho raha hai

  const user = getStoredUser();
  const role = getUserRole(user);
  const displayName = user?.fullName || user?.full_name || "Dajoo member";

  const goTo = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const logout = () => {
    ["accessToken", "userData", "deviceId", "notifications", "nin"].forEach(
      (key) => localStorage.removeItem(key),
    );
    navigate("/", { replace: true });
  };

  // ✅ API Call: Created Groups Fetch Karein
  useEffect(() => {
    const fetchGroups = async () => {
      setLoading(true);
      try {
        const response = await getData("/api/group/fetch_created");
        const data = Array.isArray(response)
          ? response
          : response?.data || response?.groups || [];
        setGroups(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching created groups:", error);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, []);

  // ✅ Sirf Pending Requests collect karein
  const pendingRequests = groups.flatMap((group) =>
    (group.UserGroups || [])
      .filter((ug) => ug.status === "Panding" || ug.status === "Pending")
      .map((ug) => ({
        ...ug,
        groupTitle: group.title,
        groupAmount: group.amount,
        groupFee: group.fee,
      })),
  );

  // ✅ Accept/Decline Button Click Handler
  const handleStatusUpdate = async (requestId, status) => {
    setActionLoading(requestId);
    try {
      const payload = {
        id: requestId,
        status: status, // "Approved" ya "Rejected"
      };

      const response = await postData("/api/group/status_update", payload);

      if (response?.status === true) {
        // ✅ Success: UI se request remove karein
        setGroups((prev) =>
          prev.map((group) => ({
            ...group,
            UserGroups: group.UserGroups.filter((ug) => ug.id !== requestId),
          })),
        );
      } else {
        alert(response?.message || "Failed to update status");
      }
    } catch (error) {
      console.error(error);
      alert("Network error. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f6f7fb", color: "#1d2433" }}>
      <Navbar
        onMenuClick={() => setMobileNavOpen(true)}
        onNavigate={goTo}
        onLogout={logout}
        user={{ fullName: displayName, userType: role }}
      />
      <Sidebar
        role={role}
        activePath={location.pathname}
        onNavigate={goTo}
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />
      <Box
        component="main"
        sx={{ ml: { xs: 0, md: "248px" }, p: { xs: 2, sm: 3, md: 5 } }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800 }}
            >
              Group Members
            </Typography>
            <Typography sx={{ color: "#7a8394", mt: 0.7 }}>
              Keep track of the people requesting to join your savings
              communities.
            </Typography>
          </Box>

          {/* Requests Section */}
          <Paper
            sx={{
              border: "1px solid #e9ebf0",
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
              overflow: "hidden",
            }}
          >
            {/* Section Header */}
            <Box
              sx={{
                p: 2.5,
                borderBottom: "1px solid #f0f1f4",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                Requests
              </Typography>
              <Chip
                icon={<HourglassTop />}
                label={`${pendingRequests.length} pending`}
                size="small"
                sx={{
                  bgcolor: "#fff3e0",
                  color: "#e65100",
                  fontWeight: 700,
                }}
              />
            </Box>

            {/* Requests List */}
            <Box sx={{ p: 2.5 }}>
              {loading ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <CircularProgress sx={{ color: "#52057b" }} />
                </Box>
              ) : pendingRequests.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      display: "grid",
                      placeItems: "center",
                      mx: "auto",
                      mb: 2,
                      borderRadius: "50%",
                      bgcolor: "#f7f1fa",
                      color: "#52057b",
                    }}
                  >
                    <PersonAdd sx={{ fontSize: 32 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 700 }}>
                    No pending requests
                  </Typography>
                  <Typography sx={{ color: "#7a8394", fontSize: 13, mt: 0.5 }}>
                    All requests have been processed.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {pendingRequests.map((request) => (
                    <Box
                      key={request.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 2.5,
                        border: "1px solid #f0f1f4",
                        borderRadius: 2,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                          borderColor: "#52057b",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: "#f7f1fa",
                          color: "#52057b",
                          fontWeight: 800,
                          fontSize: 22,
                        }}
                      >
                        {request.User?.fullName?.charAt(0) || "U"}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
                          {request.User?.fullName || "Unknown User"}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#7a8394",
                            fontSize: 13,
                            mt: 0.3,
                          }}
                        >
                          {request.User?.email || "No email"}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#7a8394",
                            fontSize: 12,
                            mt: 0.5,
                          }}
                        >
                          Requesting to join:{" "}
                          <span
                            style={{
                              fontWeight: 800,
                              color: "#52057b",
                            }}
                          >
                            {request.groupTitle}
                          </span>
                        </Typography>
                        <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                          <Chip
                            size="small"
                            label={`Fee: NGN ${Number(
                              request.groupFee || 0,
                            ).toLocaleString()}`}
                            sx={{
                              bgcolor: "#f7f1fa",
                              color: "#52057b",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Cancel />}
                          disabled={actionLoading === request.id}
                          onClick={() =>
                            handleStatusUpdate(request.id, "Rejected")
                          }
                          sx={{
                            borderColor: "#c0392b",
                            color: "#c0392b",
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 700,
                            "&:hover": { bgcolor: "#ffebee" },
                          }}
                        >
                          Decline
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={
                            actionLoading === request.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <CheckCircle />
                            )
                          }
                          disabled={actionLoading === request.id}
                          onClick={() =>
                            handleStatusUpdate(request.id, "Approved")
                          }
                          sx={{
                            bgcolor: "#52057b",
                            color: "#fff",
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 700,
                            "&:hover": { bgcolor: "#3e055e" },
                          }}
                        >
                          Accept
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default GroupMembers;
